"use client";

import { useDebounceValue } from "usehooks-ts";

import { type InfoLineProps } from "#/components/InfoLine/InfoLine";
import { defaultUnderlying } from "#/config/markets";
import { tooltips } from "#/config/tooltips";
import { useHedgeSummaryController } from "#/hooks/useHedgeSummary.controller";
import { useTradeType } from "#/hooks/useTradeType";
import { useValidateOptionSize } from "#/hooks/useValidateOptionSize";
import { useCalculateUserRisk } from "#/services/kyan/hooks/useCalculateUserRisk";
import { useIsOptionHedged } from "#/store/options/tradePanel/isOptionHedged";
import { useOption } from "#/store/options/tradePanel/option";
import { useOptionSize } from "#/store/options/tradePanel/optionSize";
import { useOptionTradeType } from "#/store/options/tradePanel/optionTradeType";
import { useAccountState } from "#/store/wallet/account/accountState";
import { getImpactColor } from "#/utils/getImpactColor";
import { getMmrImr } from "#/utils/getMmrImr";
import { createPerpInstrumentName } from "#/utils/instrument.utils";

export const useOptionPortfolioImpactAndGreeksController = () => {
  const { optionSize } = useOptionSize();
  const { option } = useOption();
  const { isRFQ: skipImpact } = useOptionTradeType();
  const { isMarket } = useTradeType();
  const { isOptionHedged } = useIsOptionHedged();
  const { hedgeDetails } = useHedgeSummaryController();

  const [debouncedSize] = useDebounceValue(optionSize, 500);

  const isBuy = !option || option.isBuy;

  const sizeError = useValidateOptionSize({
    size: debouncedSize,
    underlying: option?.underlying ?? defaultUnderlying,
  });

  const isValid = !sizeError && !skipImpact;
  const shouldCalculateHedge = isMarket && isOptionHedged && hedgeDetails;

  const inputs = [
    {
      instrument: option?.instrument_name ?? "",
      isBuy,
      size: debouncedSize,
      isEnabled: isValid,
    },
    ...(shouldCalculateHedge
      ? [
          {
            instrument: createPerpInstrumentName(option!.underlying),
            isBuy: hedgeDetails.hedgeIsBuy,
            size: hedgeDetails.hedgeAmount.toString(),
            isEnabled: isValid,
          },
        ]
      : []),
  ];

  const { im, mm, greeks, isLoading } = useCalculateUserRisk({
    inputs,
  });

  const { portfolioState } = useAccountState(option?.underlying);

  const equity = portfolioState?.equity ?? 0;

  const { imr, mmr } = getMmrImr({ equity, im, mm });

  const isMissingDetails = !debouncedSize;

  const marginData: InfoLineProps[] = [
    {
      data: {
        label: "Margin Usage (IMΔ)",
        amount: im - (portfolioState?.im ?? 0),
        unit: "USD",
      },
      tooltip: tooltips.marginUsage,
    },
    {
      data: {
        label: "Available Margin",
        amount: equity - im,
        unit: "USD",
      },
      tooltip: tooltips.availableMargin,
    },
    {
      data: {
        label: "IMr",
        change: {
          from: (portfolioState?.imr ?? 0) * 100,
          to: imr * 100,
          template: "0.00%",
        },
      },
      tooltip: tooltips.IMr,
      leftLabelClassName: getImpactColor(portfolioState?.imr ?? 0),
      rightLabelClassName: getImpactColor(imr),
    },
    {
      data: {
        label: "MMr",
        change: {
          from: (portfolioState?.mmr ?? 0) * 100,
          to: mmr * 100,
          template: "0.00%",
        },
      },
      tooltip: tooltips.MMr,
      leftLabelClassName: getImpactColor(portfolioState?.mmr ?? 0),
      rightLabelClassName: getImpactColor(mmr),
    },
  ];

  const greeksData: InfoLineProps[] = [
    {
      data: {
        label: "Δ Delta",
        change: {
          from: portfolioState?.greeks.delta ?? 0,
          to: greeks.delta ?? 0,
          template: "0.0000",
        },
      },
      tooltip: tooltips.Delta,
    },
    {
      data: {
        label: "Γ Gamma",
        change: {
          from: portfolioState?.greeks.gamma ?? 0,
          to: greeks.gamma ?? 0,
          template: "0.000",
        },
      },
      tooltip: tooltips.gamma,
    },
    {
      data: {
        label: "V Vega",
        change: {
          from: portfolioState?.greeks.vega ?? 0,
          to: greeks.vega ?? 0,
          template: "0.00",
        },
      },
      tooltip: tooltips.vega,
    },
    {
      data: {
        label: "Θ Theta",
        change: {
          from: portfolioState?.greeks.theta ?? 0,
          to: greeks.theta ?? 0,
          template: "0.00",
        },
      },
      tooltip: tooltips.theta,
    },
  ];

  return {
    isValid,
    marginData,
    greeksData,
    isLoading,
    isMissingDetails,
  };
};
